import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { slackApi } from '../api/slack.api';
import type { UpdateSlackSettingsInput } from '../types/slack.types';

// ===========================================
// QUERY KEYS
// ===========================================

export const slackKeys = {
  all: ['slack'] as const,
  status: () => [...slackKeys.all, 'status'] as const,
  settings: () => [...slackKeys.all, 'settings'] as const,
  channels: () => [...slackKeys.all, 'channels'] as const,
  users: () => [...slackKeys.all, 'users'] as const,
};

// ===========================================
// QUERIES
// ===========================================

export function useSlackStatus() {
  return useQuery({
    queryKey: slackKeys.status(),
    queryFn: () => slackApi.getStatus(),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 30 * 1000,
  });
}

export function useSlackSettings() {
  return useQuery({
    queryKey: slackKeys.settings(),
    queryFn: () => slackApi.getSettings(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSlackChannels() {
  return useQuery({
    queryKey: slackKeys.channels(),
    queryFn: () => slackApi.listChannels(),
    enabled: false,
  });
}

export function useSlackUsers() {
  return useQuery({
    queryKey: slackKeys.users(),
    queryFn: () => slackApi.getLinkedUsers(),
    staleTime: 60 * 1000,
  });
}

// ===========================================
// MUTATIONS
// ===========================================

export function useSlackAuthUrl() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => slackApi.getAuthUrl(),
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to get authorization URL', {
        variant: 'error',
      });
    },
  });
}

export function useDisconnectSlack() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => slackApi.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: slackKeys.status() });
      queryClient.invalidateQueries({ queryKey: slackKeys.settings() });
      queryClient.invalidateQueries({ queryKey: slackKeys.users() });
      enqueueSnackbar('Slack disconnected successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to disconnect Slack', {
        variant: 'error',
      });
    },
  });
}

export function useTestSlackConnection() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => slackApi.testConnection(),
    onSuccess: (data) => {
      enqueueSnackbar(
        `Connection successful! Workspace: ${data.workspace.name}`,
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Connection test failed', {
        variant: 'error',
      });
    },
  });
}

export function useUpdateSlackSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (input: UpdateSlackSettingsInput) =>
      slackApi.updateSettings(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: slackKeys.settings() });
      queryClient.invalidateQueries({ queryKey: slackKeys.status() });
      enqueueSnackbar('Slack settings updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update settings', {
        variant: 'error',
      });
    },
  });
}

export function useLinkSlackUser() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (slackUserId: string) => slackApi.linkUser({ slackUserId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: slackKeys.users() });
      queryClient.invalidateQueries({ queryKey: slackKeys.status() });
      enqueueSnackbar('Slack account linked successfully', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to link Slack account', {
        variant: 'error',
      });
    },
  });
}

export function useUnlinkSlackUser() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: () => slackApi.unlinkUser(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: slackKeys.users() });
      queryClient.invalidateQueries({ queryKey: slackKeys.status() });
      enqueueSnackbar('Slack account unlinked', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to unlink Slack account', {
        variant: 'error',
      });
    },
  });
}
